/**
 * In-memory mock backend cho Lovable preview.
 *
 * Bật khi hostname là *.lovableproject.com / *.lovable.app / *.lovable.dev.
 * Mọi data sống trong RAM — reload = sạch (đúng yêu cầu).
 *
 * Mục tiêu: trong suốt với AppStatic. Cùng shape phản hồi với PHP API.
 */

import { ApiError } from "./api";

/* ------------------------------------------------------------- detect */
export const IS_DEMO =
  typeof window !== "undefined" &&
  !(window as any).__DEMO_OFF &&
  (location.hostname.endsWith(".lovableproject.com") ||
    location.hostname.endsWith(".lovable.app") ||
    location.hostname.endsWith(".lovable.dev"));

if (IS_DEMO && typeof console !== "undefined") {
  console.info("[demo] in-memory backend active — data sẽ mất khi reload");
}

/* ------------------------------------------------------------- helpers */
const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);

const now = () => new Date().toISOString();

type Row = Record<string, any>;
const tables: Record<string, Row[]> = {};
function tbl(name: string): Row[] {
  if (!tables[name]) tables[name] = [];
  return tables[name];
}

/* ------------------------------------------------------------- seeds */
function seed() {
  // Photo checklist defaults (11 mục cơ bản)
  tables.photo_checklist_defaults = [
    "Wig / Tóc giả",
    "Trang phục chính",
    "Áo trong / inner",
    "Phụ kiện đầu",
    "Vũ khí / props",
    "Giày",
    "Tất / leggings",
    "Lens",
    "Trang điểm",
    "Phụ kiện nhỏ (nhẫn, vòng…)",
    "Background / phông",
  ].map((name, i) => ({ id: uid(), name, sort_order: i }));

  // 7 project mặc định
  const projectSeeds: Array<{ key: string; name: string; group: string; sort: number }> = [
    { key: "rental-costume", name: "Trang phục và phụ kiện cosplay", group: "cho-thue", sort: 0 },
    { key: "rental-gun", name: "Cho thuê mô hình súng", group: "cho-thue", sort: 1 },
    { key: "making-props", name: "Làm props theo yêu cầu", group: "lam-do", sort: 0 },
    { key: "photoshoot", name: "Chụp ảnh", group: "chup-anh", sort: 0 },
    { key: "order", name: "Order", group: "order", sort: 0 },
    { key: "archive-rental", name: "Lưu trữ – Cho thuê", group: "luu-tru", sort: 0 },
    { key: "archive-making", name: "Lưu trữ – Làm đồ", group: "luu-tru", sort: 1 },
  ];
  tables.task_projects = projectSeeds.map((s) => ({
    id: uid(),
    key: s.key,
    name: s.name,
    project_group: s.group,
    color: null,
    icon: null,
    sort_order: s.sort,
    is_pinned: true,
    revenue_stream: (s.key === "order" || s.key === "rental-gun" || s.group === "order") ? "side" : "main",
    created_at: now(),
    updated_at: now(),
  }));

  // 3 section todo/doing/done cho mỗi project
  tables.task_sections = [];
  for (const p of tables.task_projects) {
    ["todo", "doing", "done"].forEach((key, i) => {
      const label = key === "todo" ? "Cần làm" : key === "doing" ? "Đang làm" : "Đã xong";
      tables.task_sections.push({
        id: uid(),
        project_id: p.id,
        key,
        name: label,
        sort_order: i,
        board_columns: null,
      });
    });
  }

  // Một vài customer mẫu
  tables.customers = [
    { id: uid(), name: "Khách Demo A", phone: "0901234567", email: null, note: null, created_at: now(), updated_at: now() },
    { id: uid(), name: "Khách Demo B", phone: "0907654321", email: null, note: null, created_at: now(), updated_at: now() },
  ];

  // Inventory mẫu — phân rõ dòng chính (cosplay/props) và dòng phụ (mô hình súng)
  const img = (seed: string, w = 800, h = 1000) => `https://picsum.photos/seed/${seed}/${w}/${h}`;
  tables.inventory = [
    { id: uid(), name: "Bộ Marin Kitagawa", category: "trang-phuc", variant: "Size M", note: "Có wig kèm", image_url: img("marin"),  group_name: "Anime", available: true,  sort_order: 0, updated_at: now() },
    { id: uid(), name: "Wig hồng dài 80cm", category: "phu-kien-cosplay", variant: null, note: null, image_url: img("wig-pink"), group_name: "Tóc giả", available: true, sort_order: 1, updated_at: now() },
    { id: uid(), name: "Kiếm gỗ Demon Slayer", category: "phu-kien-cosplay", variant: null, note: "Sơn đen-đỏ", image_url: img("sword"), group_name: "Props", available: true, sort_order: 2, updated_at: now() },
    { id: uid(), name: "Súng M4A1 (mô hình)", category: "mo-hinh-sung", variant: null, note: "Đạn nhựa", image_url: img("m4a1"), group_name: null, available: true, sort_order: 0, updated_at: now() },
    { id: uid(), name: "Súng AK-47 (mô hình)", category: "mo-hinh-sung", variant: null, note: null, image_url: img("ak47"), group_name: null, available: false, sort_order: 1, updated_at: now() },
    { id: uid(), name: "Súng ngắn Glock 17", category: "mo-hinh-sung", variant: null, note: null, image_url: img("glock"), group_name: null, available: true, sort_order: 2, updated_at: now() },
  ];

  // Media library mẫu — có folder tree đầy đủ
  const mediaSeeds: Array<{ title: string; folder: string; tags: string[]; seed: string; w?: number; h?: number }> = [
    { title: "Marin Kitagawa cover", folder: "san-pham/cosplay/marin", tags: ["cosplay","anime"], seed: "marin", w: 800, h: 1000 },
    { title: "Marin Kitagawa detail", folder: "san-pham/cosplay/marin", tags: ["cosplay"], seed: "marin2", w: 1200, h: 800 },
    { title: "Wig hồng", folder: "san-pham/cosplay/wig", tags: ["wig"], seed: "wig-pink", w: 600, h: 900 },
    { title: "Demon Slayer kiếm", folder: "san-pham/props", tags: ["props","kiem"], seed: "sword", w: 1000, h: 700 },
    { title: "M4A1 model", folder: "kho/mo-hinh-sung", tags: ["sung","tactical"], seed: "m4a1", w: 900, h: 600 },
    { title: "AK-47 model", folder: "kho/mo-hinh-sung", tags: ["sung"], seed: "ak47", w: 900, h: 600 },
    { title: "Glock 17", folder: "kho/mo-hinh-sung", tags: ["sung","pistol"], seed: "glock", w: 700, h: 700 },
    { title: "Studio backdrop", folder: "bo-suu-tap/photoshoot", tags: ["studio"], seed: "studio", w: 1600, h: 900 },
    { title: "Outdoor shoot", folder: "bo-suu-tap/photoshoot", tags: ["outdoor"], seed: "outdoor", w: 900, h: 1600 },
    { title: "BTS makeup", folder: "timeline/2026-05", tags: ["bts"], seed: "makeup", w: 800, h: 800 },
  ];
  tables.media_library = mediaSeeds.map((m, i) => ({
    id: uid(),
    url: img(m.seed, m.w, m.h),
    path: null,
    title: m.title,
    folder: m.folder,
    tags: m.tags,
    sort_order: 1000 - i,
    created_at: now(),
  }));

  // Products mẫu — đủ cả mode rental/craft, có ảnh + cover
  const productSeeds: Array<{ name: string; mode: string; category: string; images: string[]; price?: string; fandom?: string }> = [
    { name: "Marin Kitagawa Full Set",   mode: "rental", category: "trang-phuc",        images: [img("marin"), img("marin2")], price: "500.000đ / ngày", fandom: "My Dress-Up Darling" },
    { name: "Wig hồng 80cm",              mode: "rental", category: "phu-kien-cosplay",  images: [img("wig-pink")], price: "80.000đ / ngày" },
    { name: "Kiếm gỗ Nichirin (custom)",  mode: "craft",  category: "props",             images: [img("sword")], price: "Liên hệ", fandom: "Demon Slayer" },
    { name: "M4A1 mô hình",               mode: "rental", category: "mo-hinh-sung",      images: [img("m4a1")], price: "150.000đ / ngày" },
    { name: "Glock 17 mô hình",           mode: "rental", category: "mo-hinh-sung",      images: [img("glock")], price: "100.000đ / ngày" },
    { name: "Combo Studio Shoot",         mode: "sample", category: "chup-anh",          images: [img("studio"), img("outdoor")], price: "Xem ảnh mẫu" },
  ];
  tables.products = productSeeds.map((p, i) => ({
    id: uid(),
    slug: p.name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/đ/g, "d").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
    name: p.name,
    description: `Mô tả demo cho ${p.name}.`,
    category: p.category,
    fandom: p.fandom ?? null,
    character_name: null,
    materials: null,
    price: p.price ?? null,
    mode: p.mode,
    images: p.images,
    cover_image: p.images[0],
    published: true,
    sort_order: 100 - i,
    attributes: {},
    image_meta: {},
    piwigo_album_url: null,
    piwigo_album_id: null,
    tile_w: 3,
    tile_h: 1,
    created_at: now(),
    updated_at: now(),
  }));

  // Tasks mẫu — 2 main + 2 side, đủ deposit / done để test finance sync
  const projMain = tables.task_projects.find((p) => p.key === "rental-costume")!;
  const projSide = tables.task_projects.find((p) => p.key === "rental-gun")!;
  const projOrder = tables.task_projects.find((p) => p.key === "order")!;
  const cust = tables.customers[0];
  tables.tasks = [
    { id: uid(), project_id: projMain.id, title: "Thuê Marin set – Khách A", status: "doing", customer_id: cust.id,
      deposit_amount: 200000, total_amount: 500000, deposit_paid: true, due_date: null,
      revenue_stream: "main", created_at: now(), updated_at: now() },
    { id: uid(), project_id: projMain.id, title: "Trả đồ Wig hồng – Khách B", status: "done", customer_id: tables.customers[1].id,
      deposit_amount: 50000, total_amount: 80000, deposit_paid: true, returned: true,
      revenue_stream: "main", created_at: now(), updated_at: now() },
    { id: uid(), project_id: projSide.id, title: "Thuê M4A1 – Khách A", status: "doing", customer_id: cust.id,
      deposit_amount: 100000, total_amount: 150000, deposit_paid: true,
      revenue_stream: "side", created_at: now(), updated_at: now() },
    { id: uid(), project_id: projOrder.id, title: "Order figure SCM Frieren", status: "todo", customer_id: cust.id,
      deposit_amount: 0, total_amount: 1200000, deposit_paid: false,
      revenue_stream: "side", created_at: now(), updated_at: now() },
  ];

  // Finance mẫu — bám đúng revenue_stream
  const today = new Date().toISOString().slice(0, 10);
  tables.finance = [
    { id: uid(), kind: "income",  amount: 200000, category: "coc",       note: "Cọc Marin – Khách A",   occurred_on: today, revenue_stream: "main", created_at: now() },
    { id: uid(), kind: "income",  amount: 80000,  category: "thu-cuoi",  note: "Thanh toán Wig hồng",    occurred_on: today, revenue_stream: "main", created_at: now() },
    { id: uid(), kind: "expense", amount: 50000,  category: "ship",      note: "Ship đồ trả",            occurred_on: today, revenue_stream: "main", created_at: now() },
    { id: uid(), kind: "income",  amount: 100000, category: "coc",       note: "Cọc M4A1 – Khách A",     occurred_on: today, revenue_stream: "side", created_at: now() },
    { id: uid(), kind: "expense", amount: 30000,  category: "bao-tri",   note: "Bảo trì súng",           occurred_on: today, revenue_stream: "side", created_at: now() },
  ];

  tables.task_fields = [];
  tables.timeline_posts = [];
  tables.site_texts = [];
  tables.attribute_types = [];
  tables.product_attributes = [];

  // Rental inventory revenue_stream (cho phase trước — đảm bảo lọc đúng khi tab inventory)
  for (const it of tables.inventory) {
    (it as any).revenue_stream = (it.category === "mo-hinh-sung") ? "side" : "main";
  }
}
seed();

/* ------------------------------------------------------------- CRUD helper */
function filterByQuery(rows: Row[], query?: Record<string, any>): Row[] {
  if (!query) return rows;
  return rows.filter((r) => {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === "") continue;
      if (k === "q") {
        const s = String(v).toLowerCase();
        const hay = JSON.stringify(r).toLowerCase();
        if (!hay.includes(s)) return false;
      } else if (k === "from") {
        const d = r.occurred_on || r.created_at || r.due_date;
        if (d && d < v) return false;
      } else if (k === "to") {
        const d = r.occurred_on || r.created_at || r.due_date;
        if (d && d > v) return false;
      } else if (k in r) {
        if (String(r[k] ?? "") !== String(v)) return false;
      }
    }
    return true;
  });
}

function crud(
  table: string,
  method: string,
  rest: string[],
  body: any,
  query: any,
): any {
  const rows = tbl(table);

  // GET /table → list
  // GET /table/:id → one
  // POST /table → create
  // PUT /table/:id → update
  // DELETE /table/:id → delete
  if (rest.length === 0) {
    if (method === "GET") return filterByQuery(rows, query);
    if (method === "POST") {
      const row = { id: uid(), ...body, created_at: now(), updated_at: now() };
      rows.push(row);
      return row;
    }
  }
  if (rest.length === 1) {
    const id = decodeURIComponent(rest[0]);
    const idx = rows.findIndex((r) => String(r.id) === id || String(r.key) === id);
    if (method === "GET") {
      if (idx < 0) throw new ApiError(404, "Not found");
      return rows[idx];
    }
    if (method === "PUT" || method === "PATCH") {
      if (idx < 0) throw new ApiError(404, "Not found");
      rows[idx] = { ...rows[idx], ...body, updated_at: now() };
      return rows[idx];
    }
    if (method === "DELETE") {
      if (idx < 0) return { ok: true };
      rows.splice(idx, 1);
      return { ok: true };
    }
  }
  return null;
}

/* ------------------------------------------------------------- router */
type DemoOpts = { query?: any; json?: any; form?: FormData };

/* ---------- task ↔ finance sync (mirror sync_task_finance.php) ---------- */
function projectStream(projectId?: string | null): "main" | "side" {
  if (!projectId) return "side";
  const p = tbl("task_projects").find((x) => x.id === projectId);
  return p?.revenue_stream === "main" ? "main" : "side";
}
function upsertFinance(existingId: string | null | undefined, data: Row): string {
  const rows = tbl("finance");
  if (existingId) {
    const i = rows.findIndex((r) => r.id === existingId);
    if (i >= 0) {
      rows[i] = { ...rows[i], ...data, updated_at: now() };
      return existingId;
    }
  }
  const id = uid();
  rows.push({
    id,
    occurred_on: data.occurred_on || new Date().toISOString().slice(0, 10),
    currency: "VND",
    created_at: now(),
    updated_at: now(),
    ...data,
  });
  return id;
}
function deleteAutoFinance(id?: string | null) {
  if (!id) return;
  const rows = tbl("finance");
  const i = rows.findIndex((r) => r.id === id && r.auto);
  if (i >= 0) rows.splice(i, 1);
}
function syncTaskFinance(taskId: string) {
  const tasks = tbl("tasks");
  const ti = tasks.findIndex((t) => t.id === taskId);
  if (ti < 0) return;
  const task = tasks[ti];
  const title = String(task.title || "").trim() || "Việc không tên";
  const total = Number(task.total_amount || 0);
  const deposit = Number(task.deposit_amount || 0);
  const isDone = task.status === "done" || !!task.completed_at;
  const stream = projectStream(task.project_id);
  let depId: string | null = task.deposit_finance_id || null;
  let finId: string | null = task.final_finance_id || null;

  if (deposit > 0) {
    depId = upsertFinance(depId, {
      kind: "income", amount: deposit, category: "coc",
      item_name: "Cọc — " + title, note: "Cọc — " + title,
      task_id: taskId, project_id: task.project_id || null,
      customer_id: task.customer_id || null, auto: 1,
      revenue_stream: stream,
    });
  } else {
    deleteAutoFinance(depId);
    depId = null;
  }

  const remaining = Math.max(0, total - Math.max(0, deposit));
  if (isDone && total > 0 && remaining > 0) {
    finId = upsertFinance(finId, {
      kind: "income", amount: remaining, category: "thu-cuoi",
      item_name: title, note: title,
      task_id: taskId, project_id: task.project_id || null,
      customer_id: task.customer_id || null, auto: 1,
      revenue_stream: stream,
      occurred_on: task.completed_at ? String(task.completed_at).slice(0, 10) : new Date().toISOString().slice(0, 10),
    });
  } else if (isDone && total > 0 && deposit <= 0) {
    finId = upsertFinance(finId, {
      kind: "income", amount: total, category: "thu-cuoi",
      item_name: title, note: title,
      task_id: taskId, project_id: task.project_id || null,
      customer_id: task.customer_id || null, auto: 1,
      revenue_stream: stream,
      occurred_on: task.completed_at ? String(task.completed_at).slice(0, 10) : new Date().toISOString().slice(0, 10),
    });
  } else {
    deleteAutoFinance(finId);
    finId = null;
  }

  tasks[ti] = { ...task, deposit_finance_id: depId, final_finance_id: finId, updated_at: now() };
}

export async function handleDemo<T>(method: string, path: string, opts?: DemoOpts): Promise<T> {
  // Giả lập độ trễ nhẹ để loader có cơ hội render
  await new Promise((r) => setTimeout(r, 30));

  const clean = path.replace(/^\/+/, "").split("?")[0];
  const parts = clean.split("/").filter(Boolean);
  const body = opts?.json ?? {};
  const query = opts?.query ?? {};

  if (typeof console !== "undefined") {
    console.debug("[demo]", method, "/" + clean, query, body);
  }

  const head = parts[0];
  const rest = parts.slice(1);

  /* ---------- auth: auto-login ---------- */
  if (head === "auth") {
    const fakeUser = { id: "demo", email: "demo@local", name: "Demo Admin", role: "admin" };
    if (rest[0] === "me") return { user: fakeUser } as T;
    if (rest[0] === "login") return { user: fakeUser } as T;
    if (rest[0] === "logout") return { ok: true } as T;
  }

  /* ---------- tasks/* ---------- */
  if (head === "tasks") {
    if (rest[0] === "projects") return crud("task_projects", method, rest.slice(1), body, query) as T;
    if (rest[0] === "sections") return crud("task_sections", method, rest.slice(1), body, query) as T;
    if (rest[0] === "fields") return crud("task_fields", method, rest.slice(1), body, query) as T;
    if (rest[0] === "bulk-upsert") {
      const arr = Array.isArray(body?.tasks) ? body.tasks : [];
      const out: Row[] = [];
      for (const t of arr) {
        if (t.id) {
          const i = tbl("tasks").findIndex((x) => x.id === t.id);
          if (i >= 0) {
            tbl("tasks")[i] = { ...tbl("tasks")[i], ...t, updated_at: now() };
            out.push(tbl("tasks")[i]);
            syncTaskFinance(tbl("tasks")[i].id);
            continue;
          }
        }
        const row = { id: uid(), ...t, created_at: now(), updated_at: now() };
        tbl("tasks").push(row);
        out.push(row);
        syncTaskFinance(row.id);
      }
      return { tasks: out } as T;
    }
    // /tasks  hoặc  /tasks/:id
    const res = crud("tasks", method, rest, body, query) as any;
    if (res && res.id && (method === "POST" || method === "PUT" || method === "PATCH")) {
      syncTaskFinance(res.id);
    }
    if (method === "DELETE" && rest.length === 1) {
      const tid = decodeURIComponent(rest[0]);
      const fin = tbl("finance");
      for (let i = fin.length - 1; i >= 0; i--) {
        if (fin[i].task_id === tid && fin[i].auto) fin.splice(i, 1);
      }
    }
    return res as T;
  }

  /* ---------- customers ---------- */
  if (head === "customers") {
    if (rest.length === 2 && rest[1] === "history") {
      const cid = decodeURIComponent(rest[0]);
      const allTasks = tbl("tasks").filter((t) => t.customer_id === cid);
      const allFinance = tbl("finance").filter((f) => f.customer_id === cid);
      let totalIncome = 0, totalExpense = 0;
      for (const f of allFinance) {
        const amt = Number(f.amount) || 0;
        if (f.kind === "income") totalIncome += amt;
        else totalExpense += amt;
      }
      return {
        tasks: allTasks,
        finance: allFinance,
        totalIncome,
        totalExpense,
        netSpend: totalIncome - totalExpense,
      } as T;
    }
    return crud("customers", method, rest, body, query) as T;
  }

  /* ---------- inventory ---------- */
  if (head === "inventory") return crud("inventory", method, rest, body, query) as T;

  /* ---------- finance ---------- */
  if (head === "finance") return crud("finance", method, rest, body, query) as T;

  /* ---------- photo-checklist-defaults ---------- */
  if (head === "photo-checklist-defaults")
    return crud("photo_checklist_defaults", method, rest, body, query) as T;

  /* ---------- site-texts ---------- */
  if (head === "site-texts") {
    // key dạng string thường, không phải uuid → dùng PUT/DELETE theo key
    const rows = tbl("site_texts");
    if (rest.length === 0) {
      if (method === "GET") return rows as T;
      if (method === "POST") {
        const row = { key: body.key, value: body.value ?? "", updated_at: now() };
        const i = rows.findIndex((r) => r.key === row.key);
        if (i >= 0) rows[i] = row;
        else rows.push(row);
        return row as T;
      }
    }
    if (rest.length === 1) {
      const key = decodeURIComponent(rest[0]);
      const i = rows.findIndex((r) => r.key === key);
      if (method === "PUT") {
        const row = { key, value: body.value ?? "", updated_at: now() };
        if (i >= 0) rows[i] = row;
        else rows.push(row);
        return row as T;
      }
      if (method === "DELETE") {
        if (i >= 0) rows.splice(i, 1);
        return { ok: true } as T;
      }
    }
  }

  /* ---------- timeline-posts ---------- */
  if (head === "timeline-posts") {
    if (method === "POST" && rest[0] === "upload") {
      return { url: "https://placehold.co/600x800?text=Demo+Piwigo", warnings: ["Demo mode — không upload thật"] } as T;
    }
    return crud("timeline_posts", method, rest, body, query) as T;
  }

  /* ---------- products ---------- */
  if (head === "products") return crud("products", method, rest, body, query) as T;

  /* ---------- attribute-types / product-attributes ---------- */
  if (head === "attribute-types") return crud("attribute_types", method, rest, body, query) as T;
  if (head === "product-attributes") return crud("product_attributes", method, rest, body, query) as T;

  /* ---------- product-images (mock — dùng blob URL để xem ngay) ---------- */
  if (head === "product-images") {
    if (method === "POST" && rest.length === 0) {
      const file = opts?.form?.get?.("file") as File | null;
      const url = file ? URL.createObjectURL(file) : `https://picsum.photos/seed/up-${Date.now()}/800/1000`;
      return { ok: true, url, warnings: ["Demo mode — ảnh chỉ tồn tại đến khi reload trang"] } as T;
    }
    return { ok: true } as T;
  }

  /* ---------- piwigo (mock no-op) ---------- */
  if (head === "piwigo") {
    if (rest[0] === "test") return { ok: false, message: "Demo mode — Piwigo không khả dụng" } as T;
    return { ok: true } as T;
  }

  /* ---------- media (đầy đủ: list, upload blob, reorder, search) ---------- */
  if (head === "media") {
    const rows = tbl("media_library");
    if (method === "POST" && rest[0] === "upload") {
      const form = opts?.form;
      const file = form?.get?.("file") as File | null;
      const folder = String(form?.get?.("folder") ?? "");
      const title = String(form?.get?.("title") ?? file?.name ?? "Ảnh demo");
      const tagsRaw = String(form?.get?.("tags") ?? "");
      const tags = tagsRaw.split(",").map((s) => s.trim()).filter(Boolean);
      const url = file ? URL.createObjectURL(file) : `https://picsum.photos/seed/up-${Date.now()}/800/1000`;
      const row = {
        id: uid(), url, path: null, title, folder, tags,
        sort_order: (rows[0]?.sort_order ?? 1000) + 1,
        created_at: now(),
        warnings: ["Demo mode — ảnh chỉ tồn tại đến khi reload trang"],
      };
      rows.unshift(row);
      return row as T;
    }
    if (rest[0] === "search" && method === "GET") {
      const q = String(query?.q ?? "").toLowerCase();
      const limit = Number(query?.limit ?? 8);
      if (!q) return [] as T;
      return rows
        .filter((r) => (r.title ?? "").toLowerCase().includes(q) || (r.folder ?? "").toLowerCase().includes(q))
        .slice(0, limit)
        .map((r) => ({ id: r.id, url: r.url, title: r.title })) as T;
    }
    if (rest[0] === "reorder" && method === "POST") {
      const updates = Array.isArray(body?.updates) ? body.updates : [];
      for (const u of updates) {
        const i = rows.findIndex((r) => r.id === u.id);
        if (i >= 0) rows[i] = { ...rows[i], sort_order: Number(u.sort_order) || 0 };
      }
      return { ok: true, count: updates.length } as T;
    }
    return crud("media_library", method, rest, body, query) as T;
  }

  /* ---------- finance categories + health (mới — phase stream-lock) ---------- */
  if (head === "finance" && rest[0] === "categories") {
    const stream = query?.stream;
    const base = ["coc", "thu-cuoi", "ship", "vat-tu", "bao-tri", "khac"];
    return base.map((k) => ({ key: k, label: k, stream: stream ?? "main" })) as T;
  }
  if (head === "finance" && rest[0] === "health") {
    return { ok: true, missing_stream_projects: [], mismatched_entries: [] } as T;
  }

  /* ---------- backup / audit / push / stickers / users / jobs / taxonomy ---------- */
  if (["backup", "audit", "push", "stickers", "users", "jobs", "taxonomy"].includes(head)) {
    if (method === "GET") return [] as T;
    return { ok: true } as T;
  }

  /* ---------- fallback ---------- */
  console.warn("[demo] unmocked endpoint:", method, "/" + clean);
  if (method === "GET") return [] as T;
  return { ok: true } as T;
}
